/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file mailApi.js
 * @description
 * Thin, fully-typed networking layer for the in-app mail client. Wraps every
 * `/api/mail/*` REST endpoint exposed by the backend (mail.controller.js /
 * mail.swagger.yaml) behind small named functions so screens never touch raw
 * fetch/axios. Authentication is the standard `Bearer <accessToken>` header,
 * read from secure storage exactly like the rest of the app's service modules.
 *
 * Endpoint base: `${ApiConfig.apiURL}/api/mail`.
 *
 * All functions resolve to parsed JSON (or `true` for 204) and reject with an
 * Error carrying the backend message on non-2xx responses.
 */

import {Linking, Platform} from 'react-native';
import {ApiConfig} from '../config/ApiConfig';
import {getAccessToken} from '../helpers/storage';
import i18n from '../../i18n';

const MAIL_BASE = `${ApiConfig.apiURL}/api/mail`;

/**
 * Abort window for the two reads the mail READER blocks on (single message +
 * thread). Without it a stalled TCP connection leaves the reader spinning
 * forever — fetch has no default timeout. Every other endpoint keeps its
 * previous, unbounded behaviour so nothing else changes shape or semantics.
 * @type {number}
 */
const READ_TIMEOUT_MS = 15000;

/**
 * Build an Error from a failed Response, preferring the backend message body.
 * @param {Response} response
 * @returns {Promise<Error>}
 */
const buildError = async (response) => {
    let message = response.statusText;
    let payload = null;
    try {
        const body = await response.json();
        payload = body;
        message = body?.message || body?.error || message;
    } catch (_e) {
        // non-JSON body — keep statusText
    }
    const error = new Error(message || `Mail request failed with status ${response.status}`);
    error.status = response.status;
    // Additive: keep the parsed error body (e.g. the collaboration takeover 409 carries
    // `currentAssigneeId`) and the backend's machine code (`msg`) for callers that need them.
    if (payload && typeof payload === 'object') {
        error.payload = payload;
        if (payload.msg) {
            error.msg = payload.msg;
        }
    }
    if (response.status === 401 || response.status === 403) {
        error.code = 'AUTH_REQUIRED';
    }
    return error;
};

/**
 * Core authenticated request helper.
 * @param {string} path Path relative to the mail base (must start with '/').
 * @param {RequestInit & {query?: Object, timeoutMs?: number}} [options] `timeoutMs`
 *   aborts the request after that many milliseconds (opt-in — omitted = unbounded,
 *   exactly as before).
 * @returns {Promise<*>} Parsed JSON, or `true` for empty/204 responses.
 * @throws {Error} With `code: 'AUTH_REQUIRED'` when signed out, `code: 'TIMEOUT'`
 *   when the abort window elapsed, or the backend message for a non-2xx response.
 */
const request = async (path, options = {}) => {
    const accessToken = await getAccessToken();
    if (!accessToken) {
        const authError = new Error('Authentication required');
        authError.code = 'AUTH_REQUIRED';
        throw authError;
    }

    const {query, headers, timeoutMs, ...rest} = options;
    const url = `${MAIL_BASE}${path}${buildQuery(query)}`;

    // Abort support is opt-in per call and degrades gracefully on any runtime
    // without AbortController (older web targets) — there the request simply
    // keeps its previous unbounded behaviour.
    const controller = timeoutMs > 0 && typeof globalThis.AbortController === 'function'
        ? new globalThis.AbortController()
        : null;
    let timer = null;
    if (controller) {
        timer = globalThis.setTimeout(() => {
            try {
                controller.abort();
            } catch (_e) {
                // ignore — the fetch below rejects on its own
            }
        }, timeoutMs);
    }

    let response;
    try {
        response = await fetch(url, {
            ...rest,
            ...(controller ? {signal: controller.signal} : {}),
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
                ...(headers || {}),
            },
        });
    } catch (networkError) {
        if (controller?.signal?.aborted) {
            const timeoutError = new Error(i18n.t('mail_error_timeout', {
                defaultValue: 'The mail server took too long to respond. Please try again.',
            }));
            timeoutError.code = 'TIMEOUT';
            throw timeoutError;
        }
        if (!networkError?.message) {
            const networkFailure = new Error(i18n.t('mail_error_network', {
                defaultValue: 'Could not reach the mail server. Check your connection.',
            }));
            networkFailure.code = 'NETWORK';
            throw networkFailure;
        }
        throw networkError;
    } finally {
        if (timer) {
            globalThis.clearTimeout(timer);
        }
    }

    if (!response.ok) {
        throw await buildError(response);
    }

    if (response.status === 204) {
        return true;
    }

    const text = await response.text();
    if (!text) {
        return true;
    }
    try {
        return JSON.parse(text);
    } catch (_e) {
        return text;
    }
};

/**
 * Serialize a query object into a `?a=b&c=d` string, skipping nullish values.
 * @param {Object} [query]
 * @returns {string}
 */
const buildQuery = (query) => {
    if (!query || typeof query !== 'object') {
        return '';
    }
    const params = Object.entries(query)
        .filter(([, value]) => value !== undefined && value !== null && value !== '')
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    return params.length ? `?${params.join('&')}` : '';
};

const get = (path, query, options = {}) => request(path, {method: 'GET', query, ...options});
const post = (path, body, query) => request(path, {method: 'POST', body: body ? JSON.stringify(body) : undefined, query});
const put = (path, body) => request(path, {method: 'PUT', body: body ? JSON.stringify(body) : undefined});
const patch = (path, body) => request(path, {method: 'PATCH', body: body ? JSON.stringify(body) : undefined});
const del = (path, query) => request(path, {method: 'DELETE', query});

/** POST /mail/messages/trash/empty — physically purge every message in Trash → `{purgedCount}`. */
export const emptyMailTrash = () => post('/messages/trash/empty');

/** POST /mail/messages/spam/empty — physically purge every message in Spam (and an IMAP Junk) → `{purgedCount, folder}`. */
export const emptyMailSpam = () => post('/messages/spam/empty');

/* ───────────────────────── Account sharing (owner side) ───────────────────────── */

/** GET /mail/accounts/:id/shares — own-account shares → `{shares, pendingShares}`. */
export const listMailAccountShares = (accountId) => get(`/accounts/${enc(accountId)}/shares`);

/** GET /mail/accounts/:id/share-candidates — colleagues it can still be shared with → `{users}`. */
export const listMailShareCandidates = (accountId) => get(`/accounts/${enc(accountId)}/share-candidates`);

/**
 * POST /mail/accounts/:id/shares — share with `{userIds[], permission}` (or `{userId, permission}`
 * to change the permission of an existing share).
 * → `{shares, pendingShares, results[], createdCount, pendingCount, failedCount}`.
 */
export const shareMailAccount = (accountId, body) => post(`/accounts/${enc(accountId)}/shares`, body);

/** DELETE /mail/accounts/:id/shares/:userId — revoke a share, or withdraw a pending request. */
export const revokeMailAccountShare = (accountId, userId) => del(`/accounts/${enc(accountId)}/shares/${enc(userId)}`);

/* ───────────────────── Share requests (recipient side) ───────────────────── */

/** GET /mail/share-requests — requests waiting for the caller's Allow / Deny → `{requests}`. */
export const listMailShareRequests = () => get('/share-requests');


/* ───────────────────────── Settings & account ───────────────────────── */

/** GET /mail/settings — sanitized mailbox configuration. */
export const getMailSettings = () => get('/settings');

/** PUT /mail/settings — persist mailbox configuration. @param {Object} payload */
export const updateMailSettings = (payload) => put('/settings', payload);

/** POST /mail/settings/test — verify IMAP/SMTP credentials. @param {Object} payload */
export const testMailSettings = (payload) => post('/settings/test', payload);

/** POST /mail/settings/autodiscover — detect server settings from an e-mail. @param {{email:string}} payload */
export const autodiscoverMailSettings = (payload) => post('/settings/autodiscover', payload);

/**
 * DELETE /mail/accounts/:id — soft-delete a mail account. The account is hidden from the
 * mailbox and all of its emails are soft-deleted server-side; returns the refreshed settings.
 * @param {string} id Account id to remove.
 */
export const deleteMailAccount = (id) => del(`/accounts/${id}`);

/* ───────────────────────── Mailbox OAuth (Gmail + Outlook) ───────────────────────── */

/** GET /mail/oauth/config — combined availability of every OAuth provider (google + microsoft). */
export const getMailOAuthConfig = () => get('/oauth/config');

/** GET /mail/oauth/google/config — whether Gmail OAuth is available on the server + redirect URI. */
export const getGoogleMailOAuthConfig = () => get('/oauth/google/config');

/**
 * GET /mail/oauth/{provider}/start — returns the provider consent URL to open in the system browser.
 * @param {'google'|'microsoft'} provider
 * @param {string} [accountId] Existing account id to reconnect in place.
 * @returns {Promise<{authUrl:string, redirectUri:string}>}
 */
export const startMailOAuth = (provider, accountId) => get(`/oauth/${provider}/start`, accountId ? {accountId} : undefined);

/**
 * Kick off an OAuth mailbox connect from a user gesture and open the provider consent page in a way
 * that survives the browser popup blocker. On web/Tauri a blank popup is opened **synchronously**
 * (still inside the tap) and only navigated to the consent URL once `/start` resolves — a
 * `window.open()` deferred until after the await is blocked by every modern browser, which is why the
 * connect silently did nothing on web. On native the system browser is used via `Linking`. Either
 * way the screen keeps polling `getMailSettings` for the freshly-created account, because the browser
 * tab (no `window.opener`) can't post back into the app.
 *
 * Call it as `openMailOAuthConsent(startMailOAuth(provider[, accountId]))` so the popup opens in the
 * same synchronous turn as the tap.
 *
 * @param {Promise<{authUrl?: string}>} startPromise Result of {@link startMailOAuth}.
 * @returns {Promise<string>} The consent URL that was opened.
 * @throws {Error} When the server returns no authorization URL.
 */
export const openMailOAuthConsent = (startPromise) => {
    const onWeb = Platform.OS === 'web' && typeof window !== 'undefined';
    let popup = null;
    if (onWeb) {
        try {
            popup = window.open('', 'happy-mail-oauth', 'width=520,height=680');
        } catch (_openError) {
            popup = null;
        }
    }

    return Promise.resolve(startPromise)
        .then((res) => {
            const authUrl = res && res.authUrl;
            if (!authUrl) {
                throw new Error('Could not start sign-in.');
            }
            if (onWeb) {
                if (popup) {
                    popup.location.href = authUrl;
                } else {
                    window.open(authUrl, '_blank');
                }

                return authUrl;
            }

            return Linking.openURL(authUrl).then(() => authUrl);
        })
        .catch((err) => {
            if (popup) {
                try {
                    popup.close();
                } catch (_closeError) {
                    // ignore
                }
            }
            throw err;
        });
};

/** GET /mail/oauth/google/start — Gmail consent URL. @param {string} [accountId] */
export const startGoogleMailOAuth = (accountId) => startMailOAuth('google', accountId);

/** GET /mail/oauth/microsoft/start — Outlook/Microsoft consent URL. @param {string} [accountId] */
export const startMicrosoftMailOAuth = (accountId) => startMailOAuth('microsoft', accountId);

/* ───────────────────────── Status / sync / notifications ───────────────── */

/** GET /mail/status — configured flag, unread + queue + quota summary. */
export const getMailStatus = () => get('/status');

/** POST /mail/sync — force an immediate mailbox sync. */
export const syncMailbox = () => post('/sync');

/** GET /mail/notifications/poll — mail notifications since a timestamp. @param {string} [since] ISO */
export const pollMailNotifications = (since) => get('/notifications/poll', {since});

/* ───────────────────────── Messages & threads ───────────────────────── */

/**
 * GET /mail/messages — paginated message list. Quick filters (`unread`,
 * `starred`, `hasAttachments`) and `sort` are server-side, so they cover the
 * whole mailbox/search scope, not just the page(s) already fetched.
 * @param {{folder?:string,label?:string,unread?:boolean,starred?:boolean,
 *          hasAttachments?:boolean,direction?:string,status?:string,
 *          since?:string,search?:string,page?:number,limit?:number,
 *          sort?:('newest'|'oldest')}} [params]
 */
export const listMailMessages = (params = {}) => get('/messages', params);

/**
 * GET /mail/messages/:id — full message with body + attachments.
 * Abort-bounded ({@link READ_TIMEOUT_MS}) because the reader blocks on it: a hung
 * socket must surface a readable error, never an endless spinner.
 * @param {string} id
 */
export const getMailMessage = (id) => get(`/messages/${id}`, undefined, {timeoutMs: READ_TIMEOUT_MS});

/** POST /mail/messages — queue an outgoing message. @param {Object} payload */
export const queueMailMessage = (payload) => post('/messages', payload);

/** POST /mail/messages/:id/cancel — cancel a queued send within the delay window. @param {string} id */
export const cancelMailMessage = (id) => post(`/messages/${id}/cancel`);

/** PATCH /mail/messages/:id/flags — update read/starred flags. @param {string} id @param {{isRead?:boolean,isStarred?:boolean}} payload */
export const updateMailMessageFlags = (id, payload) => patch(`/messages/${id}/flags`, payload);

/** POST /mail/messages/bulk-flags — bulk flag update. @param {{ids:string[],isRead?:boolean,isStarred?:boolean}} payload */
export const bulkUpdateMailMessageFlags = (payload) => post('/messages/bulk-flags', payload);

/** POST /mail/messages/:id/move — move a single message. @param {string} id @param {{folder:string}} payload */
export const moveMailMessage = (id, payload) => post(`/messages/${id}/move`, payload);

/** POST /mail/messages/move — bulk move. @param {{ids:string[],folder:string}} payload */
export const moveMailMessages = (payload) => post('/messages/move', payload);

/**
 * POST /mail/messages/delete — bulk delete with mail-client two-step semantics:
 * ids outside `Trash` are moved there, ids already in `Trash` are purged, and
 * `permanent: true` purges every id immediately (irreversible).
 * @param {{ids:string[],permanent?:boolean}} payload
 */
export const deleteMailMessages = (payload) => post('/messages/delete', payload);

/** POST /mail/messages/labels — bulk add/remove/replace labels. @param {Object} payload */
export const updateMailMessageLabels = (payload) => post('/messages/labels', payload);

/**
 * POST /mail/messages/color — set (or clear with `null`/`''`) the colour mark of messages.
 * @param {{messageIds:string[], color?:string|null, applyToThread?:boolean}} payload
 * @returns {Promise<{matchedCount:number, modifiedCount:number, color:string|null, items:Object[]}>}
 */
export const setMailMessagesColor = (payload) => post('/messages/color', payload);

/**
 * POST /mail/messages/organize — colour + labels + folder in one call (colour omitted = untouched,
 * `null`/`''` = clear; at least one operation required).
 * @param {{messageIds:string[], folderId?:string, addLabelIds?:string[], removeLabelIds?:string[], color?:string|null, applyToThread?:boolean}} payload
 * @returns {Promise<{matchedCount:number, modifiedCount:number, items:Object[], applied:string[]}>}
 */
export const organizeMailMessages = (payload) => post('/messages/organize', payload);

/**
 * GET /mail/organize/summary — per-dimension totals + unread: `{folders, colors, categories, labels}`.
 * @param {string} [accountId] Optional mailbox account id.
 * @returns {Promise<{folders:Object[], colors:Object[], categories:Object[], labels:Object[]}>}
 */
export const getMailOrganizeSummary = (accountId) => get('/organize/summary', accountId ? {accountId} : undefined);

/**
 * GET /mail/threads/:threadKey — all messages in a conversation.
 * Abort-bounded ({@link READ_TIMEOUT_MS}) for the same reason as {@link getMailMessage}.
 * @param {string} threadKey
 */
export const getMailThread = (threadKey) => get(`/threads/${encodeURIComponent(threadKey)}`, undefined, {timeoutMs: READ_TIMEOUT_MS});

/**
 * CRM bridge: the leads (inside the caller's lead scope) and customers behind an e-mail address,
 * for the reader's "who is this" card (`GET /api/mail/contact-context`).
 * @param {string} email address to look up.
 * @returns {Promise<{email: string, leads: Object[], customers: Object[]}>} matches.
 */
export const getMailContactContext = (email) => get('/contact-context', {email}, {timeoutMs: READ_TIMEOUT_MS});

/* ───────────────────────── Drafts ───────────────────────── */

/** GET /mail/drafts — paginated drafts. @param {{search?:string,page?:number,limit?:number}} [params] */
export const listMailDrafts = (params = {}) => get('/drafts', params);

/** GET /mail/drafts/:id — single draft. @param {string} id */
export const getMailDraft = (id) => get(`/drafts/${id}`);

/** POST /mail/drafts — create draft. @param {Object} payload */
export const createMailDraft = (payload) => post('/drafts', payload);

/** PUT /mail/drafts/:id — update draft. @param {string} id @param {Object} payload */
export const updateMailDraft = (id, payload) => put(`/drafts/${id}`, payload);

/** DELETE /mail/drafts/:id — delete draft. @param {string} id */
export const deleteMailDraft = (id) => del(`/drafts/${id}`);

/** POST /mail/drafts/:id/send — send draft (queues it). @param {string} id */
export const sendMailDraft = (id) => post(`/drafts/${id}/send`);

/* ───────────────────────── Scheduled sends ───────────────────────── */

/** GET /mail/scheduled — pending scheduled sends (mongoose-paginate). @param {{page?:number,limit?:number,accountId?:string}} [params] */
export const listScheduledMessages = (params = {}) => get('/scheduled', params);

/** PATCH /mail/scheduled/:id — move the send time. @param {string} id @param {string} scheduledAt ISO date-time */
export const rescheduleMailMessage = (id, scheduledAt) => patch(`/scheduled/${enc(id)}`, {scheduledAt});

/** POST /mail/scheduled/:id/send-now — send immediately. @param {string} id */
export const sendScheduledMailNow = (id) => post(`/scheduled/${enc(id)}/send-now`);

/** DELETE /mail/scheduled/:id — cancel the scheduled send. @param {string} id */
export const cancelScheduledMail = (id) => del(`/scheduled/${enc(id)}`);

/** POST /mail/scheduled/:id/to-draft — cancel and reopen as a draft ({draft, messageId}). @param {string} id */
export const scheduledMailToDraft = (id) => post(`/scheduled/${enc(id)}/to-draft`);

/* ───────────────────────── Folders & labels ───────────────────────── */

/** GET /mail/folders — folders + unread counts. */
export const listMailFolders = () => get('/folders');

/** POST /mail/folders — create custom folder. @param {{name:string,color?:string,scope?:string,accountIds?:string[]}} payload */
export const createMailFolder = (payload) => post('/folders', payload);

/** PUT /mail/folders/:id — update folder. @param {string} id @param {Object} payload */
export const updateMailFolder = (id, payload) => put(`/folders/${id}`, payload);

/** DELETE /mail/folders/:id — delete folder. @param {string} id */
export const deleteMailFolder = (id) => del(`/folders/${id}`);

/* ───────────────────────── Rules / automations ───────────────────────── */

/** GET /mail/rules — list mailbox rules. */
export const listMailRules = () => get('/rules');

/** POST /mail/rules — create rule. @param {Object} payload */
export const createMailRule = (payload) => post('/rules', payload);

/** PUT /mail/rules/:id — update rule. @param {string} id @param {Object} payload */
export const updateMailRule = (id, payload) => put(`/rules/${id}`, payload);

/** DELETE /mail/rules/:id — delete rule. @param {string} id */
export const deleteMailRule = (id) => del(`/rules/${id}`);

/** PATCH /mail/rules/:id/enable — toggle rule. @param {string} id @param {{enabled:boolean}} payload */
export const toggleMailRule = (id, enabled) => patch(`/rules/${id}/enable`, {enabled});

/* ───────────────────────── Security / trust ───────────────────────── */

/** POST /mail/security/allow-remote — trust sender/domain for remote images. @param {Object} payload */
export const allowMailRemoteContent = (payload) => post('/security/allow-remote', payload);

/** POST /mail/security/block — block a sender/domain. @param {Object} payload */
export const blockMailSender = (payload) => post('/security/block', payload);

/** DELETE /mail/security/block — unblock a sender/domain. @param {{senderEmail?:string,domain?:string}} query */
export const unblockMailSender = (query) => del('/security/block', query);

/** POST /mail/messages/spam — mark message(s) as spam. @param {Object} payload */
export const markMailAsSpam = (payload) => post('/messages/spam', payload);

/* ───────────────────────── AI features ───────────────────────── */

/**
 * POST /mail/ai/compose — generate/reply/proofread/rephrase/expand/shorten/translate/
 * professional/grammar. Also accepts `tone`, `length`, and `variants` (1-3) for multiple drafts.
 * @param {Object} payload
 */
export const composeMailWithAI = (payload) => post('/ai/compose', payload);

/**
 * POST /mail/ai/summarize — summarize a message or whole thread.
 * Accepts `messageId` | `threadKey` | inline content, plus `mode`
 * (brief|tldr|detailed|actions|extract).
 * @param {Object} payload
 */
export const summarizeMailMessage = (payload) => post('/ai/summarize', payload);

/** POST /mail/ai/translate — translate a message to targetLocale. @param {Object} payload */
export const translateMailMessage = (payload) => post('/ai/translate', payload);

/**
 * POST /mail/ai/triage — classify a message (category/priority/sentiment/intent/spam +
 * quick replies). Accepts `messageId` | `threadKey` | inline content.
 * @param {Object} payload
 */
export const triageMailMessage = (payload) => post('/ai/triage', payload);

/**
 * POST /mail/ai/reply-suggestions — one-tap reply options for a message.
 * Accepts `messageId` | `threadKey` | inline content.
 * @param {Object} payload
 */
export const suggestMailReplies = (payload) => post('/ai/reply-suggestions', payload);

/* ───────────────────────── Design templates ───────────────────────── */

/** GET /mail/design-templates — available design templates. */
export const listMailDesignTemplates = () => get('/design-templates');

/** POST /mail/design-templates/preview — render a template preview. @param {Object} payload */
export const previewMailDesignTemplate = (payload) => post('/design-templates/preview', payload);

/* ───────────────────────── Recipients ───────────────────────── */

/** GET /mail/recipients/search — recipient autocomplete. @param {string} q @param {number} [limit] */
export const searchMailRecipients = (q, limit = 8) => get('/recipients/search', {q, limit});

/* ───────────────────────── Shared-mailbox collaboration ───────────────────────── */
/* Backend: mailCollab.swagger.yaml / mailCollab.service.js. Mirrors admin `mailNetworking`. */

const enc = encodeURIComponent;

/** GET /mail/collab/mailboxes — the caller's shared + personal mailboxes with role, tags, auto-assign. */
export const collabListMailboxes = () => get('/collab/mailboxes');

/** PATCH /mail/collab/mailboxes/:address — shared-mailbox settings (manager only). @param {string} address @param {Object} body */
export const collabUpdateMailbox = (address, body) => patch(`/collab/mailboxes/${enc(address)}`, body);

/** GET /mail/collab/mailboxes/:address/members — members with role / online / open workload. @param {string} address */
export const collabListMembers = (address) => get(`/collab/mailboxes/${enc(address)}/members`);

/** POST /mail/collab/mailboxes/:address/tags — create a team tag (manager only). @param {string} address @param {{name:string,color?:string}} body */
export const collabCreateTeamTag = (address, body) => post(`/collab/mailboxes/${enc(address)}/tags`, body);

/** PATCH /mail/collab/mailboxes/:address/tags/:tagId — update a team tag. @param {string} address @param {string} tagId @param {Object} body */
export const collabUpdateTeamTag = (address, tagId, body) => patch(`/collab/mailboxes/${enc(address)}/tags/${enc(tagId)}`, body);

/** DELETE /mail/collab/mailboxes/:address/tags/:tagId — delete a team tag. @param {string} address @param {string} tagId */
export const collabDeleteTeamTag = (address, tagId) => del(`/collab/mailboxes/${enc(address)}/tags/${enc(tagId)}`);

/** POST /mail/collab/threads/states — thread states for up to 200 conversation keys. @param {string} address @param {string[]} conversationKeys */
export const collabGetThreadStates = (address, conversationKeys) => post('/collab/threads/states', {address, conversationKeys});

/** GET /mail/collab/threads/activity — state + activity timeline. @param {string} address @param {string} conversationKey */
export const collabGetThreadActivity = (address, conversationKey) => get('/collab/threads/activity', {address, conversationKey});

/** GET /mail/collab/threads/assigned — include/exclude conversation-key filter of a smart view. @param {{address:string,assignee?:string,tag?:string,status?:string}} query */
export const collabListAssignedThreads = (query = {}) => get('/collab/threads/assigned', query);

/** POST /mail/collab/threads/assign — assign / claim / take over. @param {{address:string,conversationKey:string,assigneeId:(string|null),takeover?:boolean,note?:string,subject?:string}} body */
export const collabAssignThread = (body) => post('/collab/threads/assign', body);

/** POST /mail/collab/threads/unassign. @param {{address:string,conversationKey:string,subject?:string}} body */
export const collabUnassignThread = (body) => post('/collab/threads/unassign', body);

/** POST /mail/collab/threads/done. @param {{address:string,conversationKey:string,subject?:string}} body */
export const collabMarkDone = (body) => post('/collab/threads/done', body);

/** POST /mail/collab/threads/reopen. @param {{address:string,conversationKey:string,subject?:string}} body */
export const collabReopenThread = (body) => post('/collab/threads/reopen', body);

/** POST /mail/collab/threads/tags — replace the team tags of a thread. @param {{address:string,conversationKey:string,tags:string[],subject?:string}} body */
export const collabSetThreadTags = (body) => post('/collab/threads/tags', body);

/**
 * GET /mail/collab/threads/chat — private discussion of a shared-mailbox thread (never creates the channel).
 * Answers `{chatAvailable:false,...}` when team chat is off for the store.
 * @param {{address:string,conversationKey:string,before?:string,limit?:number}} query
 */
export const collabGetThreadChat = (query) => get('/collab/threads/chat', query);

/**
 * GET /mail/collab/threads/chat/target — mail behind a thread discussion (Team Chat "Open mail").
 * Resolves `{address, conversationKey, mailMessageId, threadKey, subject}`; `mailMessageId` is '' when
 * the caller has no readable copy (403 non-member, 422 NOT_SHARED).
 * @param {{address:string,conversationKey:string}} query
 */
export const collabGetThreadChatTarget = (query) => get('/collab/threads/chat/target', query);

/** POST /mail/collab/threads/chat/open — create-or-get the discussion channel. @param {{address:string,conversationKey:string,subject?:string}} body */
export const collabOpenThreadChat = (body) => post('/collab/threads/chat/open', body);

/**
 * POST /mail/collab/threads/chat/messages — post a comment (optionally assigning the thread).
 * @param {{address:string,conversationKey:string,text:string,subject?:string,clientMessageId?:string,mentions?:string[],assignTo?:string,takeover?:boolean}} body
 */
export const collabPostThreadChatMessage = (body) => post('/collab/threads/chat/messages', body);

/** POST /mail/collab/threads/chat/read — mark the discussion read. @param {{address:string,conversationKey:string,messageId?:string}} body */
export const collabMarkThreadChatRead = (body) => post('/collab/threads/chat/read', body);

/**
 * POST /mail/snoozes — "notify me again later".
 * @param {{messageId?:string,conversationKey?:string,until?:string,preset?:string,hours?:number,timezone?:string,reason?:string}} body
 */
export const snoozeThread = (body) => post('/snoozes', body);

/** GET /mail/snoozes — the caller's snoozes. @param {{status?:string}} [query] */
export const listSnoozes = (query = {status: 'pending'}) => get('/snoozes', query);

/** DELETE /mail/snoozes/:id — cancel a snooze. @param {string} id */
export const cancelSnooze = (id) => del(`/snoozes/${enc(id)}`);

/** GET /mail/tags — personal tags (mailbox labels) with unread counts + team tags with open counts. */
export const listTags = () => get('/tags');

/** POST /mail/tags — create a personal tag. @param {{name:string,color?:string}} body */
export const createPersonalTag = (body) => post('/tags', body);

/** PATCH /mail/tags/:id — update a personal tag. @param {string} id @param {Object} body */
export const updatePersonalTag = (id, body) => patch(`/tags/${enc(id)}`, body);

/** DELETE /mail/tags/:id — delete a personal tag. @param {string} id */
export const deletePersonalTag = (id) => del(`/tags/${enc(id)}`);

export default {
    emptyMailTrash,
    emptyMailSpam,
    listMailAccountShares,
    listMailShareCandidates,
    shareMailAccount,
    revokeMailAccountShare,
    listMailShareRequests,
    setMailMessagesColor,
    organizeMailMessages,
    listScheduledMessages,
    rescheduleMailMessage,
    sendScheduledMailNow,
    cancelScheduledMail,
    scheduledMailToDraft,
    getMailOrganizeSummary,
    getMailSettings,
    updateMailSettings,
    testMailSettings,
    autodiscoverMailSettings,
    deleteMailAccount,
    getMailOAuthConfig,
    getGoogleMailOAuthConfig,
    startMailOAuth,
    startGoogleMailOAuth,
    startMicrosoftMailOAuth,
    getMailStatus,
    syncMailbox,
    pollMailNotifications,
    listMailMessages,
    getMailMessage,
    queueMailMessage,
    cancelMailMessage,
    updateMailMessageFlags,
    bulkUpdateMailMessageFlags,
    moveMailMessage,
    moveMailMessages,
    deleteMailMessages,
    updateMailMessageLabels,
    getMailThread,
    listMailDrafts,
    getMailDraft,
    createMailDraft,
    updateMailDraft,
    deleteMailDraft,
    sendMailDraft,
    listMailFolders,
    createMailFolder,
    updateMailFolder,
    deleteMailFolder,
    listMailRules,
    createMailRule,
    updateMailRule,
    deleteMailRule,
    toggleMailRule,
    allowMailRemoteContent,
    blockMailSender,
    unblockMailSender,
    markMailAsSpam,
    composeMailWithAI,
    summarizeMailMessage,
    translateMailMessage,
    triageMailMessage,
    suggestMailReplies,
    listMailDesignTemplates,
    previewMailDesignTemplate,
    searchMailRecipients,
    collabListMailboxes,
    collabUpdateMailbox,
    collabListMembers,
    collabCreateTeamTag,
    collabUpdateTeamTag,
    collabDeleteTeamTag,
    collabGetThreadStates,
    collabGetThreadActivity,
    collabListAssignedThreads,
    collabAssignThread,
    collabUnassignThread,
    collabMarkDone,
    collabReopenThread,
    collabSetThreadTags,
    snoozeThread,
    listSnoozes,
    cancelSnooze,
    listTags,
    createPersonalTag,
    updatePersonalTag,
    deletePersonalTag,
};
